/**
 * The one place a translation catalogue is pulled in on demand.
 *
 * Each `import()` is a deferral boundary: Metro compiles it to an async require,
 * so the language's sixteen JSON modules are not evaluated until the promise is
 * asked for. The specifiers are literal on purpose — a computed path cannot be
 * resolved statically, and the bundle would either be dropped or every language
 * would be pulled back in to be safe.
 *
 * English is absent by design. It is `fallbackLng`, so i18next holds it whatever
 * the user picked, and `TranslationKey` is derived from its shape — deferring it
 * would cost the compile-time key check and save nothing.
 */

import type { SupportedLanguage } from "./languages";

/** A merged translation table for one language. */
export type TranslationBundle = Record<string, unknown>;

export async function loadLanguageBundle(
  language: SupportedLanguage,
): Promise<TranslationBundle | null> {
  switch (language) {
    case "es-ES":
      return (await import("./resources/bundles/es-ES")).default;
    case "es-419":
      return (await import("./resources/bundles/es-419")).default;
    case "pt-BR":
      return (await import("./resources/bundles/pt-BR")).default;
    case "pt-PT":
      return (await import("./resources/bundles/pt-PT")).default;
    case "fr-FR":
      return (await import("./resources/bundles/fr-FR")).default;
    case "fr-CA":
      return (await import("./resources/bundles/fr-CA")).default;
    case "en":
    default:
      // Already held: statically imported as the fallback.
      return null;
  }
}
